/**
 * Product Page Script
 * ------------------------------------------------------------------------------
 * Handles all PDP interactions: variant selection, gallery, zoom modal,
 * quantity stepper, detail accordions, add-to-cart state, and BIS forms.
 *
 * Ported from ChicSoul's product.js and adapted for Stitchy Fish's globals:
 *   - slate.Variants  (from index.min.js)
 *   - StyleHatch.AjaxCart.addFromForm  (from custom.js — handles form submit)
 *   - StyleHatch.currencyFormat  (money format string from theme.liquid)
 *   - jQuery + Slick  (loaded globally)
 *   - BIS.create()  (back-in-stock app global)
 *   - notificationCallback  (defined inline in product-buy-box.liquid)
 */

(function ($) {
  // ---------------------------------------------------------------------------
  // Selectors
  // ---------------------------------------------------------------------------
  var selectors = {
    // Product data
    productJson: '[data-product-json]',
    metafieldsData: '[data-metafields-json]',

    // Variant system
    singleOptionSelector: '[data-single-option-selector]',
    originalSelectorId: '[data-product-select]',
    productForm: '[data-product-form]',

    // Prices
    priceWrapper: '[data-price-wrapper]',
    productPrice: '[data-product-price]',
    comparePrice: '[data-compare-price]',
    comparePriceText: '[data-compare-text]',

    // ATC
    addToCart: '[data-add-to-cart]',
    addToCartText: '[data-add-to-cart-text]',
    preorderMessaging: '[data-preorder-messaging]',
    preorderProperty: '[data-preorder-property]',

    // Qty
    qtyAddToCartContainer: '[data-pdp-qty-atc]',
    qtyStepper: '[data-qty-stepper]',
    qtyInput: '[data-qty-input]',

    // BIS
    backInStock: '[data-pdp-backinstock]',
    bisEmailSubmit: '[data-bis-email]',
    bisPhoneField: '[data-bis-phone-field]',
    bisPhoneSubmit: '[data-bis-phone]',

    // SKU
    productSku: '[data-product-sku-element]',

    // Product note
    productNote: '[data-product-note]',

    // Low stock
    lowStockData: '[data-low-stock-message]',
    installmentOptions: '[data-installment-options]',
    productPersonalizer: '[id$="__zepto_app"]',

    // Accordions
    dropdownToggler: '[data-product-details-dropdown-header]',
    dropdown: '[data-product-details-dropdown]',

    // Gallery (mobile Slick slider)
    productGallery: '[data-product-gallery]',
    thumbnailGallery: '[data-thumbnail-gallery]',
    thumbnailVideo: '[data-thumbnail-video]',

    // Gallery video
    galleryVideo: '[data-gallery-video]',
    galleryVideoPlay: '[data-gallery-video-play]',
    galleryVideoUnmute: '[data-gallery-video-unmute]',
    galleryVideoMute: '[data-gallery-video-mute]',

    // Zoom modal
    zoomOpen: '[data-zoom-open]',
    zoomClose: '[data-zoom-close]',
    zoomModal: '[data-zoom-modal]',
    zoomThumbnail: '[data-zoom-thumbnail]',
    zoomImage: '[data-zoom-image]',
    zoomModalGallery: '[data-zoom-gallery]',

    // Badge
    badgeContainer: '[data-product-badges]',

    // Recommendations sliders
    completeTheLook: '[data-complete-the-look]',
    shopMoreColors: '[data-shop-more-colors]',
  };

  var classes = {
    modalOpen: 'is-open',
    active: 'is-active',
    mobileVisible: 'mobile-visible',
    noScroll: 'no-scroll',
  };

  // ---------------------------------------------------------------------------
  // Preorder helpers — mirrors product-buy-box.liquid tag/settings logic
  // ---------------------------------------------------------------------------
  function parsePreorderSettingTags(settingsString) {
    if (!settingsString) return [];

    return settingsString.split(',').reduce(function (tags, setting) {
      var tag = setting.split('|')[0];

      if (tag) {
        tags.push(tag.trim());
      }

      return tags;
    }, []);
  }

  function productHasPreorderTag(tags, preorderConfig) {
    if (!tags || !tags.length) return false;

    var i;
    var j;
    var tagValue;
    var settingTags = [];

    if (preorderConfig && preorderConfig.enabled && preorderConfig.settings) {
      settingTags = parsePreorderSettingTags(preorderConfig.settings);

      for (i = 0; i < settingTags.length; i++) {
        for (j = 0; j < tags.length; j++) {
          if (tags[j] === settingTags[i]) {
            return true;
          }
        }
      }
    }

    return false;
  }

  function isVariantPreorder(tags, variant, preorderConfig) {
    if (!variant) return false;

    return productHasPreorderTag(tags, preorderConfig);
  }

  // ---------------------------------------------------------------------------
  // Formatmoney helper
  // Uses StyleHatch.currencyFormat set in theme.liquid, or falls back to
  // Shopify.formatMoney if available.
  // ---------------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || StyleHatch.currencyFormat;

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number === null) return 0;

      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  }

  // ---------------------------------------------------------------------------
  // Product constructor
  // ---------------------------------------------------------------------------
  function Product() {
    var self = this;

    // Bail early if no product JSON present (e.g. Theme Editor with empty section)
    var $productJsonEl = $(selectors.productJson);
    if (!$productJsonEl.length || !$productJsonEl.html().trim()) {
      return;
    }

    this.productSingleObject = JSON.parse($productJsonEl.html());
    this.metafieldsData = {};
    var $metaEl = $(selectors.metafieldsData);
    if ($metaEl.length) {
      try {
        this.metafieldsData = JSON.parse($metaEl.html());
      } catch (e) {}
    }

    this.$form = $(selectors.productForm);
    this.$badgeContainer = $(selectors.badgeContainer);
    this.lowStockData = $(selectors.lowStockData);
    this.backInStock = $(selectors.backInStock);
    this.qtyAddToCartContainer = $(selectors.qtyAddToCartContainer);
    this.$qtyInput = $(selectors.qtyInput);
    this.$installmentOptions = $(selectors.installmentOptions);
    this.$productPersonalizer = $(selectors.productPersonalizer);

    // Zoom gallery elements
    this.$zoomModal = $(selectors.zoomModal);
    this.$zoomModalOpen = $(selectors.zoomOpen);
    this.$zoomModalClose = $(selectors.zoomClose);
    this.$zoomModalThumbnail = $(selectors.zoomThumbnail);
    this.$zoomModalImages = $(selectors.zoomImage);
    this.$zoomModalGallery = $(selectors.zoomModalGallery);

    // --- Variant system ---
    var variantOptions = {
      $container: this.$form,
      enableHistoryState: true,
      singleOptionSelector: selectors.singleOptionSelector,
      originalSelectorId: selectors.originalSelectorId,
      product: this.productSingleObject,
    };
    this.variants = new slate.Variants(variantOptions);

    this.$form.on('variantChange.product', this.onVariantChange.bind(this));
    this.$form.on('variantPriceChange.product', this.updateProductPrices.bind(this));

    $(document).on('change', '.single-option-selector__radio', function () {
      var pos = $(this).attr('data-position');
      var val = $(this).val();
      $('[data-single-option-selector][data-index="option' + pos + '"]', self.$form)
        .val(val)
        .trigger('change');
    });

    // Accordion togglers
    $(selectors.dropdownToggler).on('click', this.productDetailToggleDropdown.bind(this));

    // Quantity steppers
    $(document).on('click', selectors.qtyStepper, this.productQty.bind(this));
    $(document).on('change', selectors.qtyInput, this.validateMaxQty.bind(this));

    // BIS forms
    $(document).on('click', selectors.bisEmailSubmit, this.bisEmail.bind(this));
    $(document).on('keyup', selectors.bisPhoneField, this.updatePhoneStructure.bind(this));
    $(document).on('click', selectors.bisPhoneSubmit, this.bisPhone.bind(this));

    // Zoom modal
    $(document).on('click', selectors.zoomOpen, this.openZoomModal.bind(this));
    $(document).on('click', selectors.zoomClose, this.closeZoomModal.bind(this));
    $(document).on('click', selectors.zoomThumbnail, this.$scrollToZoomImage.bind(this));

    if (this.$zoomModalGallery.length && this.$zoomModalImages.length) {
      var ioOptions = {
        root: this.$zoomModalGallery.get(0),
        threshold: [0.1],
      };
      this.intersectionObserver = new IntersectionObserver(this.intersectionObserverCallback.bind(this), ioOptions);
      this.$zoomModalImages.each(function (i, el) {
        self.intersectionObserver.observe(el);
      });
    }

    // Gallery & recommendations
    this.initGalleries();
    this.initGalleryVideo();
    this.initCompleteTheLookSlider();
    this.initShopMoreColorsSlider();

    this.updateAddToCartState({ variant: this.variants.currentVariant });
    this.updateGalleryForVariant({ variant: this.variants.currentVariant });

    this.initProductNote();
  }

  // ---------------------------------------------------------------------------
  // Variant change handlers
  // ---------------------------------------------------------------------------
  Product.prototype.onVariantChange = function (evt) {
    this.updateAddToCartState(evt);
    this.updatePreorderState(evt);
    this.updateVariantMaxQuantity(evt);
    this.updateLowStockMessaging(evt);
    this.updateBISVariant(evt);
    this.updateBadge(evt);
    this.updateProductSku(evt);
    this.updateGalleryForVariant(evt);
  };

  Product.prototype.updateProductPrices = function (evt) {
    var variant = evt.variant;
    var $comparePrice = $(selectors.comparePrice);
    var $compareEls = $comparePrice.add(selectors.comparePriceText);

    $(selectors.productPrice).html(formatMoney(variant.price));

    if (variant.compare_at_price > variant.price) {
      $comparePrice.html(formatMoney(variant.compare_at_price));
      $compareEls.removeClass('hide');
    } else {
      $comparePrice.html('');
      $compareEls.addClass('hide');
    }
  };

  Product.prototype.updateProductSku = function (evt) {
    var variant = evt.variant;
    var sku = variant && variant.sku ? variant.sku : '';
    $(selectors.productSku).text(sku);
  };

  function handleize(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[\s_]+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  function imageMatchesVariant(el, variant, optionValues) {
    var color = $(el).attr('data-color');
    var mediaId = parseInt($(el).attr('data-media-id'));

    if (color) {
      return optionValues.indexOf(color) > -1;
    }

    if (mediaId && variant.featured_media) {
      return variant.featured_media.id === mediaId;
    }

    return true;
  }

  Product.prototype.updateGalleryForVariant = function (evt) {
    var variant = evt.variant;
    if (!variant) return;

    var optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean).map(handleize);

    $('.product__gallery-section--desktop .product__gallery-image').each(function () {
      $(this).toggleClass('is-filtered-out', !imageMatchesVariant(this, variant, optionValues));
    });

    var $mainGallery = $('.product__gallery-section--mobile ' + selectors.productGallery);
    if ($mainGallery.hasClass('slick-initialized')) {
      $mainGallery.find('.slick-slide:not(.slick-cloned)').each(function () {
        var matches = imageMatchesVariant(this, variant, optionValues);
        $(this).toggleClass('is-gallery-match', imageMatchesVariant(this, variant, optionValues));
      });
      $mainGallery.slick('slickUnfilter');
      $mainGallery.slick('slickFilter', '.is-gallery-match');
      $mainGallery.slick('slickGoTo', 0, true);
    }

    var $nav = $(selectors.thumbnailGallery);
    $nav.find('.product__thumbnail-wrapper').each(function () {
      $(this).toggleClass('is-filtered-out', !imageMatchesVariant(this, variant, optionValues));
    });
    $nav.find('.product__thumbnail-wrapper').removeClass('is-active');
    $nav.find('.product__thumbnail-wrapper:not(.is-filtered-out)').first().addClass('is-active');

    // --- Zoom modal (desktop) ---
    $(selectors.zoomImage).each(function () {
      $(this).toggleClass('is-filtered-out', !imageMatchesVariant(this, variant, optionValues));
    });
    $(selectors.zoomThumbnail).each(function () {
      $(this).toggleClass('is-filtered-out', !imageMatchesVariant(this, variant, optionValues));
    });
  };

  Product.prototype.initProductNote = function () {
    var $noteEl = $(selectors.productNote);
    if (!$noteEl.length) return;
    var noteText = $noteEl.text().trim();
    if (noteText) {
      this.$form.data('product-note', noteText);
    }
  };

  Product.prototype.hasPreorderTag = function () {
    var preorderConfig = window.StyleHatch && window.StyleHatch.preorderSettings;

    return productHasPreorderTag(this.productSingleObject.tags, preorderConfig);
  };

  Product.prototype.isVariantPreorder = function (variant) {
    var preorderConfig = window.StyleHatch && window.StyleHatch.preorderSettings;

    return isVariantPreorder(this.productSingleObject.tags, variant, preorderConfig);
  };

  Product.prototype.updatePreorderState = function (evt) {
    var variant = evt.variant;
    var isPreorder = this.isVariantPreorder(variant);
    var $preorderMessaging = $(selectors.preorderMessaging);
    var $preorderProperty = $(selectors.preorderProperty);

    if ($preorderMessaging.length) {
      $preorderMessaging.toggle(isPreorder);
    }

    if ($preorderProperty.length) {
      $preorderProperty.prop('disabled', !isPreorder);
    }
  };

  Product.prototype.updateAddToCartState = function (evt) {
    var variant = evt.variant;

    if (!variant) {
      $(selectors.addToCart).prop('disabled', true);
      $(selectors.addToCartText).html('Unavailable');
      this.$installmentOptions.hide();
      this.$productPersonalizer.hide();
      return;
    }

    if (variant.available) {
      $(selectors.addToCart).prop('disabled', false).removeClass('disabled');

      var $atcText = $(selectors.addToCartText);
      var isPreorder = this.isVariantPreorder(variant);
      if (isPreorder) {
        $atcText.text($atcText.data('preorder-text') || 'Pre-order Now');
      } else {
        $atcText.text('Add to Bag');
      }

      $(selectors.qtyAddToCartContainer).show().removeClass('variant-disabled');
      $(selectors.backInStock).hide();
      this.$installmentOptions.show();
      this.$productPersonalizer.show();
    } else {
      $(selectors.addToCart).prop('disabled', true).addClass('disabled');
      $(selectors.addToCartText).html('Sold Out');
      $(selectors.qtyAddToCartContainer).addClass('variant-disabled');
      this.$installmentOptions.hide();
      this.$productPersonalizer.hide();

      var hideBackInStock = $(selectors.qtyAddToCartContainer).data('hide-bis');
      if (!hideBackInStock) {
        $(selectors.qtyAddToCartContainer).hide();
        $(selectors.backInStock).show();
      }
    }
  };

  Product.prototype.updateVariantMaxQuantity = function (evt) {
    var variant = evt.variant;
    if (!variant || !this.metafieldsData[variant.id]) return;
    var variantQty = this.metafieldsData[variant.id].variant_qty;
    var currentQty = parseInt(this.$qtyInput.val(), 10);

    this.$qtyInput.attr('max', variantQty);
    if (variantQty > 0 && currentQty > variantQty) {
      this.$qtyInput.val(variantQty);
    }
  };

  Product.prototype.updateLowStockMessaging = function (evt) {
    var variant = evt.variant;
    var lowThreshold = parseInt($('[data-low-stock]').data('low-stock'), 10);
    var variantQty = variant.inventory_quantity;
    var canSellWhenOos =
      variant.inventory_policy === 'continue' || !variant.inventory_management;

    if (!canSellWhenOos && variantQty <= lowThreshold && variant.available) {
      this.lowStockData.find('.low-stock-number').text(variantQty);
      this.lowStockData.show();
    } else {
      this.lowStockData.hide();
    }
  };

  Product.prototype.updateBISVariant = function (evt) {
    this.$form.attr('data-varid', evt.variant.id);
  };

  Product.prototype.updateBadge = function (evt) {
    var variant = evt.variant;
    var tags = this.productSingleObject.tags;
    var product = this.productSingleObject;
    var badges = [];
    var maxBadges = 2;
    var cardBadgeLabel = null;
    var preOrder = this.isVariantPreorder(variant);
    var earlyAccess = false;
    var i;
    var tagValue;

    for (i = 0; i < tags.length; i++) {
      tagValue = tags[i].toLowerCase();

      if (tagValue.indexOf('badge:') > -1) {
        cardBadgeLabel = tags[i].replace(/badge:/i, '');
      }

      if (tagValue.indexOf('early access') > -1) {
        earlyAccess = true;
      }
    }

    var isBackByDemand = tags.indexOf('back-by-demand') > -1;
    var isBisHidden = tags.indexOf('bis-hidden') > -1;

    if (variant.available) {
      if (preOrder && badges.length < maxBadges) {
        badges.push("<span class='product-card__badge product-card__badge--pre-order'>Pre-order</span>");
      }

      if (isBackByDemand) {
        if (badges.length < maxBadges) {
          badges.push("<span class='product-card__badge product-card__badge--back-by-demand'>Back By Demand!</span>");
        }
      } else {
        if (cardBadgeLabel && badges.length < maxBadges) {
          badges.push("<span class='product-card__badge'>" + cardBadgeLabel + '</span>');
        }

        if (product.compare_at_price_max > product.price && badges.length < maxBadges) {
          badges.push("<span class='product-card__badge'>SALE</span>");
        }
      }
    } else if (isBisHidden) {
      badges.push(
        "<span class='product-card__badge product-card__badge--sold-out'>" +
          (this.$badgeContainer.data('oos-badge-text') || 'Sold Out') +
          '</span>'
      );
    } else {
      var bisText = earlyAccess ? 'Early Access' : this.$badgeContainer.data('bis-badge-text') || 'Notify Me';
      badges.push("<span class='product-card__badge product-card__badge--back-in-stock'>" + bisText + '</span>');
    }

    var badgeHtml = badges.join('');

    if (badgeHtml) {
      this.$badgeContainer.html(badgeHtml).removeClass('product__gallery-badges--empty');
    } else {
      this.$badgeContainer.html('').addClass('product__gallery-badges--empty');
    }
  };

  // ---------------------------------------------------------------------------
  // Quantity stepper
  // ---------------------------------------------------------------------------
  Product.prototype.productQty = function (evt) {
    var $btn = $(evt.currentTarget);
    var $container = $btn.closest('[data-qty-container]');
    var $input = $container.find(selectors.qtyInput);

    if ($input.is('[readonly]')) return;

    var qty = parseInt($input.val(), 10) || 1;
    var maxQty = parseInt($input.attr('max'), 10);

    if ($btn.hasClass('qtyplus')) {
      qty++;
    } else {
      if (qty > 1) qty--;
    }

    if (maxQty > 0 && qty > maxQty) qty = maxQty;
    $input.val(qty);
  };

  Product.prototype.validateMaxQty = function (evt) {
    var $input = $(evt.currentTarget);
    var current = parseInt($input.val(), 10);
    var max = parseInt($input.attr('max'), 10);
    if (max > 0 && current > max) {
      $input.val(max);
    }
  };

  // ---------------------------------------------------------------------------
  // PDP detail accordions
  // ---------------------------------------------------------------------------
  Product.prototype.productDetailToggleDropdown = function (evt) {
    var $header = $(evt.currentTarget);
    var targetKey = $header.data('toggle');
    var $content = $('[data-pdp-accordion-content="' + targetKey + '"]');

    if ($content.is(':visible')) {
      $content.slideUp(300);
      $header.removeClass('is-open');
      return;
    }

    // Close all open accordions
    $('[data-pdp-accordion-content]:visible').each(function () {
      $(this).slideUp(300);
      $(this).siblings('[data-product-details-dropdown-header]').removeClass('is-open');
    });

    $content.slideDown(300);
    $header.addClass('is-open');
  };

  // ---------------------------------------------------------------------------
  // Gallery — Slick (mobile only; desktop is a CSS grid)
  // ---------------------------------------------------------------------------
  Product.prototype.initGalleries = function () {
    this.initMainGallery();
    this.initThumbnailGallery();

    $(selectors.productGallery).on('afterChange', function (event, slick, currentSlide) {
      var currentS = slick.$slides[currentSlide];
      if (currentS && currentS.dataset && currentS.dataset.mediaId) {
        var video = currentS.querySelector('video');
        if (video) video.play();
      } else {
        document.querySelectorAll(selectors.galleryVideo).forEach(function (wrapper) {
          var v = wrapper.querySelector('video');
          if (v) v.pause();
        });
      }

      var $nav = $(selectors.thumbnailGallery);
      var $visibleThumbs = $nav.find('.product__thumbnail-wrapper:not(.is-filtered-out)');
      $visibleThumbs.removeClass('is-active');
      var $active = $visibleThumbs.eq(currentSlide);
      $active.addClass('is-active');
      var activeEl = $active.get(0);
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });
  };

  Product.prototype.initMainGallery = function () {
    var mainSliderOptions = {
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: true,
      fade: false,
      autoplay: false,
      dots: false,
      rows: 0,
      infinite: true,
      lazyLoad: 'progressive',
      prevArrow: '<button class="arrow-previous" aria-label="Previous Image"><svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><polygon fill="#8DA5C7" transform="translate(8.194400, 8.000000) scale(-1, 1) translate(-8.194400, -8.000000)" points="5.40416 0 3 2.41152 8.57536 8 3 13.58848 5.40416 16 13.3888 8"/></svg></button>',
      nextArrow: '<button class="arrow-next" aria-label="Next Image"><svg width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg"><polygon fill="#8DA5C7" points="5.40416 0 3 2.41152 8.57536 8 3 13.58848 5.40416 16 13.3888 8"/></svg></button>',
    };

    $('.product__gallery-section--mobile ' + selectors.productGallery)
      .not('.slick-initialized')
      .slick(mainSliderOptions);
  };

  Product.prototype.initThumbnailGallery = function () {
    var $nav = $(selectors.thumbnailGallery);
    if (!$nav.length) return;

    var $mainGallery = $('.product__gallery-section--mobile ' + selectors.productGallery);

    $nav.find('.product__thumbnail-wrapper:not(.is-filtered-out)').first().addClass('is-active');

    $nav.on('click', '.product__thumbnail-wrapper', function () {
      if ($(this).hasClass('is-filtered-out')) return;
      var $visibleThumbs = $nav.find('.product__thumbnail-wrapper:not(.is-filtered-out)');
      var index = $visibleThumbs.index($(this));
      $visibleThumbs.removeClass('is-active');
      $(this).addClass('is-active');
      $mainGallery.slick('slickGoTo', index);
    });
  };

  // ---------------------------------------------------------------------------
  // Gallery video — play/pause/mute/unmute
  // ---------------------------------------------------------------------------
  Product.prototype.initGalleryVideo = function () {
    // Click on the video area toggles play/pause (not on control buttons)
    $(document).on('click', selectors.galleryVideo, function (evt) {
      if ($(evt.target).closest(selectors.galleryVideoPlay).length || $(evt.target).closest('.product__gallery-video-mute-btn').length) {
        return;
      }
      var video = $(evt.currentTarget).find('video').get(0);
      if (!video) return;
      if (video.paused) {
        video.play();
        $(evt.currentTarget).find(selectors.galleryVideoPlay).removeClass('is-visible');
      } else {
        video.pause();
        $(evt.currentTarget).find(selectors.galleryVideoPlay).addClass('is-visible');
      }
    });

    // Explicit play button
    $(document).on('click', selectors.galleryVideoPlay, function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      var $wrapper = $(evt.currentTarget).closest(selectors.galleryVideo);
      var video = $wrapper.find('video').get(0);
      if (video) {
        video.play();
        $wrapper.find(selectors.galleryVideoPlay).removeClass('is-visible');
      }
    });

    // Unmute button
    $(document).on('click', selectors.galleryVideoUnmute, function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      var $wrapper = $(evt.currentTarget).closest(selectors.galleryVideo);
      var video = $wrapper.find('video').get(0);
      if (video) {
        $(evt.currentTarget).removeClass('is-visible');
        $wrapper.find(selectors.galleryVideoMute).addClass('is-visible');
        video.muted = false;
      }
    });

    // Mute button
    $(document).on('click', selectors.galleryVideoMute, function (evt) {
      evt.preventDefault();
      evt.stopPropagation();
      var $wrapper = $(evt.currentTarget).closest(selectors.galleryVideo);
      var video = $wrapper.find('video').get(0);
      if (video) {
        $(evt.currentTarget).removeClass('is-visible');
        $wrapper.find(selectors.galleryVideoUnmute).addClass('is-visible');
        video.muted = true;
      }
    });

    // Sync play button visibility with native video events
    document.querySelectorAll(selectors.galleryVideo).forEach(function (wrapper) {
      var video = wrapper.querySelector('video');
      if (!video) return;
      video.addEventListener('play', function () {
        $(wrapper).find(selectors.galleryVideoPlay).removeClass('is-visible');
      });
      video.addEventListener('pause', function () {
        $(wrapper).find(selectors.galleryVideoPlay).addClass('is-visible');
      });
    });
  };

  // ---------------------------------------------------------------------------
  // Zoom modal
  // ---------------------------------------------------------------------------
  Product.prototype.openZoomModal = function (evt) {
    evt.preventDefault();
    var self = this;
    var $trigger = $(evt.currentTarget);
    var imageIndex = parseInt($trigger.data('image-index'), 10) || 0;
    var isMobile = $(window).outerWidth() < 750;

    this.$zoomModal.addClass(classes.modalOpen);
    $('html').addClass(classes.noScroll);

    this.$zoomModalThumbnail.removeClass(classes.active);
    this.$zoomModalImages.removeClass(classes.mobileVisible);

    this.$zoomModalThumbnail.eq(imageIndex).addClass(classes.active);
    this.$zoomModalImages.eq(imageIndex).addClass(classes.mobileVisible);

    var galleryEl = this.$zoomModalGallery.get(0);
    if (!galleryEl) return;

    if (isMobile) {
      // On mobile only one image shows at a time (mobile-visible class),
      // no scrolling needed — just ensure we start at the top
      galleryEl.scrollTop = 0;
    } else {
      // On desktop all images are stacked vertically. aspect-ratio:1/1 on
      // .zoom-gallery__image guarantees each image has height before lazy-load
      // completes, so scrollIntoView lands on the correct position every time.
      requestAnimationFrame(function () {
        var targetEl = self.$zoomModalImages.eq(imageIndex).get(0);
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
      });
    }
  };

  Product.prototype.closeZoomModal = function () {
    this.$zoomModal.removeClass(classes.modalOpen);
    $('html').removeClass(classes.noScroll);
  };

  Product.prototype.$scrollToZoomImage = function (evt) {
    var $thumb = $(evt.currentTarget);
    var currentIndex = parseInt($thumb.data('image-index'), 10) || 0;

    if ($thumb.hasClass(classes.active)) return;

    var galleryEl = this.$zoomModalGallery.get(0);
    var targetEl = this.$zoomModalImages.eq(currentIndex).get(0);
    if (!galleryEl || !targetEl) return;

    var targetScrollTop = galleryEl.scrollTop + targetEl.getBoundingClientRect().top - galleryEl.getBoundingClientRect().top;

    this.$zoomModalGallery.animate({ scrollTop: targetScrollTop }, 400);
  };

  Product.prototype.intersectionObserverCallback = function (entries) {
    var self = this;
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var targetIndex = parseInt($(entry.target).data('image-index'), 10) || 0;

      self.$zoomModalImages.removeClass(classes.mobileVisible);
      self.$zoomModalThumbnail.removeClass(classes.active);

      self.$zoomModalImages.eq(targetIndex).addClass(classes.mobileVisible);
      self.$zoomModalThumbnail.eq(targetIndex).addClass(classes.active);
    });
  };

  // ---------------------------------------------------------------------------
  // BIS — email
  // ---------------------------------------------------------------------------
  Product.prototype.bisEmail = function (evt) {
    evt.preventDefault();
    var $form = $(selectors.productForm);
    var productId = $form.attr('data-productid');
    var variantId = $form.attr('data-varid');
    var email = $('#notify_email').val();
    var acceptsMarketing = $('#accept_marketing').is(':checked');

    if (!email) return;

    BIS.create(email, variantId, productId, {
      accepts_marketing: acceptsMarketing,
      phone_number: null,
    }).then(notificationCallback);

    if (!acceptsMarketing) return;

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=LQViUK', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        revision: '2024-02-15',
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PDP BIS form - email',
            profile: {
              data: {
                type: 'profile',
                attributes: { email: email },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: 'WDN6a4' } },
          },
        },
      }),
    }).then(function () {
      console.log('Email consent submitted');
    });
  };

  // ---------------------------------------------------------------------------
  // BIS — phone
  // ---------------------------------------------------------------------------
  Product.prototype.updatePhoneStructure = function (evt) {
    var $input = $(evt.currentTarget);
    var raw = $input
      .val()
      .replace(/\+1/g, '')
      .replace(/[^0-9]+/g, '');
    if (!raw) return;

    var part1 = raw.length > 3 ? '(' + raw.substring(0, 3) + ')' : '(' + raw;
    var part2 = raw.length > 3 ? ' ' + raw.substring(3, 6) : '';
    var part3 = raw.length > 6 ? '-' + raw.substring(6, 10) : '';
    $input.val(part1 + part2 + part3);
  };

  Product.prototype.bisPhone = function (evt) {
    evt.preventDefault();
    var $form = $(selectors.productForm);
    var productId = $form.attr('data-productid');
    var variantId = $form.attr('data-varid');
    var acceptsMarketing = $('#accept_marketing').is(':checked');
    var sms =
      '+1' +
      $('#notify_sms')
        .val()
        .replace(/[^0-9]+/g, '');

    BIS.create(null, variantId, productId, {
      accepts_marketing: acceptsMarketing,
      phone_number: sms,
    }).then(notificationCallback);

    if (!acceptsMarketing) return;

    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=LQViUK', {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        revision: '2024-02-15',
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'PDP BIS form - SMS',
            profile: {
              data: {
                type: 'profile',
                attributes: { phone_number: sms },
              },
            },
          },
          relationships: {
            list: { data: { type: 'list', id: 'WDN6a4' } },
          },
        },
      }),
    }).then(function () {
      console.log('SMS consent submitted');
    });
  };

  // ---------------------------------------------------------------------------
  // Product recommendations sliders
  // ---------------------------------------------------------------------------
  Product.prototype.initCompleteTheLookSlider = function () {
    var options = {
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: true,
      dots: true,
      infinite: true,
      rows: 0,
      prevArrow: '',
      nextArrow: '.complete-the-look [data-arrow-next]',
      responsive: [
        { breakpoint: 979, settings: { infinite: false, slidesToShow: 3, slidesToScroll: 1 } },
        { breakpoint: 750, settings: { infinite: false, slidesToShow: 2.2, slidesToScroll: 1 } },
      ],
    };

    if ($(selectors.completeTheLook).length) {
      $(selectors.completeTheLook).not('.slick-initialized').slick(options);
    }
  };

  Product.prototype.initShopMoreColorsSlider = function () {
    var options = {
      slidesToShow: 3,
      slidesToScroll: 1,
      arrows: true,
      dots: true,
      infinite: true,
      rows: 0,
      prevArrow: '',
      nextArrow: '.shop-more-colors [data-arrow-next]',
      responsive: [
        { breakpoint: 979, settings: { infinite: false, slidesToShow: 3, slidesToScroll: 1 } },
        { breakpoint: 750, settings: { infinite: false, slidesToShow: 2.2, slidesToScroll: 1 } },
      ],
    };

    if ($(selectors.shopMoreColors).length) {
      $(selectors.shopMoreColors).not('.slick-initialized').slick(options);
    }
  };

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------
  $(document).ready(function () {
    // Only run on product pages
    if (!$(selectors.productJson).length) return;
    new Product();
  });
})(jQuery);
